import React from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, ReferenceLine, Cell
} from 'recharts';
import type {
  ScenarioSimulationResults,
  BudgetOptimizationScenario,
  ThreatScenario
} from '../../../services/ExecutiveRiskRevenueEngine';

interface Props {
  scenarioResults: ScenarioSimulationResults;
  optimizedScenarios: BudgetOptimizationScenario[];
  recommendedScenario: BudgetOptimizationScenario;
  formatCurrency: (amount: number) => string;
}

// Projected threat position under a given risk reduction
interface ProjectedThreat extends ThreatScenario {
  scenarioLabel: string;
  opacity: number;
  radius: number;
  isProjected: true;
}

// Shape renderers for accessibility (redundant with color for color-blind users)
const SHAPES: Record<string, (props: any) => React.ReactElement> = {
  circle: (props) => <circle cx={props.cx} cy={props.cy} r={props.r || 8} fill={props.fill} stroke={props.stroke || '#fff'} strokeWidth={props.strokeWidth || 2} opacity={props.opacity ?? 1} />,
  square: (props) => {
    const s = (props.r || 8) * 1.6;
    return <rect x={props.cx - s/2} y={props.cy - s/2} width={s} height={s} fill={props.fill} stroke={props.stroke || '#fff'} strokeWidth={props.strokeWidth || 2} opacity={props.opacity ?? 1} />;
  },
  diamond: (props) => {
    const s = (props.r || 8) * 1.15;
    return (
      <polygon
        points={`${props.cx},${props.cy - s} ${props.cx + s},${props.cy} ${props.cx},${props.cy + s} ${props.cx - s},${props.cy}`}
        fill={props.fill} stroke={props.stroke || '#fff'} strokeWidth={props.strokeWidth || 2} opacity={props.opacity ?? 1}
      />
    );
  },
  triangle: (props) => {
    const s = (props.r || 8) * 1.15;
    return (
      <polygon
        points={`${props.cx},${props.cy - s} ${props.cx + s},${props.cy + s * 0.7} ${props.cx - s},${props.cy + s * 0.7}`}
        fill={props.fill} stroke={props.stroke || '#fff'} strokeWidth={props.strokeWidth || 2} opacity={props.opacity ?? 1}
      />
    );
  },
};

const ScatterShape = (props: any) => {
  const { cx, cy, payload } = props;
  const renderer = SHAPES[payload.shape] || SHAPES.circle;
  const isProjected = payload.isProjected;
  return renderer({
    cx, cy,
    fill: isProjected ? 'transparent' : payload.color,
    stroke: isProjected ? payload.color : '#fff',
    strokeWidth: isProjected ? 2 : 2,
    r: payload.radius || 8,
    opacity: payload.opacity ?? 1,
  });
};

const formatDollar = (val: number) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val}`;
};

const ScatterTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: 'var(--ra-glass-bg)', border: '1px solid #e2e8f0', borderRadius: '8px',
      padding: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxWidth: '280px'
    }}>
      <div style={{ fontWeight: 700, color: 'var(--ra-text-primary)', marginBottom: '2px' }}>{d.name}</div>
      {d.scenarioLabel && (
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#7c3aed', marginBottom: '4px' }}>
          {d.scenarioLabel}
        </div>
      )}
      <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px' }}>{d.description}</div>
      <div style={{ fontSize: '13px', color: '#ccd6f6' }}>
        <div>Frequency: <strong>{d.annualizedFrequency.toFixed(2)}/yr</strong></div>
        <div>Magnitude: <strong>{formatDollar(d.lossMagnitudePerEvent)}</strong></div>
        <div>ALE: <strong>{formatDollar(d.annualizedLossExpectancy)}</strong></div>
      </div>
    </div>
  );
};

const ExceedanceTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: 'var(--ra-glass-bg)', border: '1px solid #e2e8f0', borderRadius: '8px',
      padding: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }}>
      <div style={{ fontSize: '13px', color: '#ccd6f6' }}>
        <div>Loss threshold: <strong>{formatDollar(d.loss)}</strong></div>
        <div>Probability of exceeding: <strong>{d.probability.toFixed(1)}%</strong></div>
      </div>
    </div>
  );
};

// Scenario colors — distinct from threat colors, progressing from light to dark
const SCENARIO_COLORS: Record<string, string> = {
  'Conservative Growth': '#10b981',
  'Strategic Investment': '#0ea5e9',
  'Transformational Security': '#7c3aed',
  'ROI Optimized': '#f59e0b',
};

const ThreatScenarioCharts: React.FC<Props> = ({
  scenarioResults, optimizedScenarios, recommendedScenario, formatCurrency
}) => {
  const { threatScenarios, exceedanceCurve, percentileMarkers } = scenarioResults;

  // Build projected threat positions for each investment scenario
  // Investment primarily reduces frequency (likelihood), not per-event magnitude
  const projectedSeries: Array<{ label: string; data: ProjectedThreat[]; riskReduction: number }> = [];

  // Sort scenarios by budget increase to show progression
  const sortedScenarios = [...optimizedScenarios].sort((a, b) => a.budgetIncrease - b.budgetIncrease);

  sortedScenarios.forEach((scenario, idx) => {
    if (scenario.projectedRiskReduction <= 0) return;

    const reduction = scenario.projectedRiskReduction;
    const projected = threatScenarios.map(t => ({
      ...t,
      // Frequency drops proportional to risk reduction
      annualizedFrequency: Math.round(t.annualizedFrequency * (1 - reduction) * 1000) / 1000,
      // Magnitude stays mostly the same — slight reduction from better response capability
      lossMagnitudePerEvent: Math.round(t.lossMagnitudePerEvent * (1 - reduction * 0.15)),
      annualizedLossExpectancy: Math.round(
        t.annualizedFrequency * (1 - reduction) *
        t.lossMagnitudePerEvent * (1 - reduction * 0.15)
      ),
      scenarioLabel: scenario.scenarioName,
      opacity: 0.35 + (idx * 0.15),
      radius: 5,
      isProjected: true as const,
    }));

    projectedSeries.push({
      label: scenario.scenarioName,
      data: projected,
      riskReduction: reduction,
    });
  });

  // Build SVG arrows connecting current positions to recommended scenario positions
  const recommendedReduction = recommendedScenario.projectedRiskReduction;

  const cardStyle = {
    background: 'var(--ra-glass-bg)',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e5e7eb',
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>

      {/* Frequency vs Magnitude Scatter Plot with Strategic Overlays */}
      <div style={{ ...cardStyle, borderLeft: '4px solid #2563eb' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ra-text-primary)', marginBottom: '4px' }}>
          Threat Scenario Landscape
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--ra-text-secondary)', margin: '0 0 16px 0' }}>
          Current exposure (solid) vs. investment scenarios (outlined) — shows diminishing returns
        </p>

        {/* Threat Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '8px' }}>
          {threatScenarios.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#a8b2d1' }}>
              <svg width="12" height="12" viewBox="0 0 14 14">
                {s.shape === 'circle' && <circle cx="7" cy="7" r="5" fill={s.color} />}
                {s.shape === 'square' && <rect x="2" y="2" width="10" height="10" fill={s.color} />}
                {s.shape === 'diamond' && <polygon points="7,1 13,7 7,13 1,7" fill={s.color} />}
                {s.shape === 'triangle' && <polygon points="7,1 13,12 1,12" fill={s.color} />}
              </svg>
              {s.name}
            </div>
          ))}
        </div>

        {/* Scenario Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px', fontSize: '11px' }}>
          <span style={{ color: 'var(--ra-text-primary)', fontWeight: 600 }}>Scenarios:</span>
          {sortedScenarios.map(sc => {
            const isRec = sc.scenarioName === recommendedScenario.scenarioName;
            return (
              <span key={sc.scenarioName} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                color: '#a8b2d1', fontWeight: isRec ? 600 : 400
              }}>
                <span style={{
                  display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%',
                  border: `2px solid ${SCENARIO_COLORS[sc.scenarioName] || '#94a3b8'}`,
                  background: 'transparent'
                }} />
                {sc.scenarioName} ({(sc.projectedRiskReduction * 100).toFixed(0)}%)
                {isRec && <span style={{ fontSize: '9px', background: '#059669', color: 'white', padding: '1px 5px', borderRadius: '8px', marginLeft: '2px' }}>REC</span>}
              </span>
            );
          })}
        </div>

        <ResponsiveContainer width="100%" height={320}>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="annualizedFrequency"
              type="number"
              name="Frequency"
              unit="/yr"
              tick={{ fontSize: 12, fill: '#8892b0' }}
              label={{ value: 'Annual Frequency', position: 'insideBottom', offset: -5, style: { fontSize: 12, fill: '#94a3b8' } }}
            />
            <YAxis
              dataKey="lossMagnitudePerEvent"
              type="number"
              name="Magnitude"
              tick={{ fontSize: 12, fill: '#8892b0' }}
              tickFormatter={formatDollar}
              label={{ value: 'Loss per Event', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 12, fill: '#94a3b8' } }}
            />
            <Tooltip content={<ScatterTooltip />} />

            {/* Projected scenarios — rendered first so current state draws on top */}
            {projectedSeries.map((series, sIdx) => (
              <Scatter
                key={`projected-${sIdx}`}
                data={series.data}
                shape={<ScatterShape />}
              >
                {series.data.map((d, i) => (
                  <Cell key={i} fill="transparent" stroke={d.color} />
                ))}
              </Scatter>
            ))}

            {/* Current state — solid shapes on top */}
            <Scatter
              data={threatScenarios.map(t => ({ ...t, isProjected: false, opacity: 1, radius: 8 }))}
              shape={<ScatterShape />}
            >
              {threatScenarios.map((s, i) => (
                <Cell key={i} fill={s.color} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>

        {/* Diminishing returns callout */}
        {sortedScenarios.length >= 2 && (
          <div style={{
            marginTop: '10px', padding: '8px 12px', background: '#fefce8',
            border: '1px solid #fde68a', borderRadius: '6px', fontSize: '12px', color: '#92400e'
          }}>
            <strong>Diminishing returns:</strong>{' '}
            {sortedScenarios[0].scenarioName} (+{formatCurrency(sortedScenarios[0].budgetIncrease)}) reduces risk{' '}
            {(sortedScenarios[0].projectedRiskReduction * 100).toFixed(1)}%
            {sortedScenarios.length >= 3 && (
              <> — doubling to {sortedScenarios[sortedScenarios.length - 2]?.scenarioName} only achieves{' '}
              {(sortedScenarios[sortedScenarios.length - 2].projectedRiskReduction * 100).toFixed(1)}%</>
            )}
          </div>
        )}
      </div>

      {/* Loss Exceedance Curve */}
      <div style={{ ...cardStyle, borderLeft: '4px solid #7c3aed' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ra-text-primary)', marginBottom: '4px' }}>
          Loss Exceedance Curve
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--ra-text-secondary)', margin: '0 0 16px 0' }}>
          Probability that annual loss exceeds a given threshold
        </p>

        {/* Percentile markers legend */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '12px' }}>
          <span style={{ color: '#059669' }}>
            <strong style={{ borderBottom: '2px dashed #059669' }}>P50</strong> {formatCurrency(percentileMarkers.p50)}
          </span>
          <span style={{ color: '#d97706' }}>
            <strong style={{ borderBottom: '2px dashed #d97706' }}>P90</strong> {formatCurrency(percentileMarkers.p90)}
          </span>
          <span style={{ color: '#dc2626' }}>
            <strong style={{ borderBottom: '2px dashed #dc2626' }}>P95</strong> {formatCurrency(percentileMarkers.p95)}
          </span>
        </div>

        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={exceedanceCurve} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
            <defs>
              <linearGradient id="exceedGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="loss"
              tickFormatter={formatDollar}
              tick={{ fontSize: 12, fill: '#8892b0' }}
              label={{ value: 'Annual Loss Threshold', position: 'insideBottom', offset: -5, style: { fontSize: 12, fill: '#94a3b8' } }}
            />
            <YAxis
              dataKey="probability"
              unit="%"
              tick={{ fontSize: 12, fill: '#8892b0' }}
              label={{ value: 'Exceedance Probability', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 12, fill: '#94a3b8' } }}
            />
            <Tooltip content={<ExceedanceTooltip />} />
            <ReferenceLine x={percentileMarkers.p50} stroke="#059669" strokeDasharray="5 5" strokeWidth={1.5} />
            <ReferenceLine x={percentileMarkers.p90} stroke="#d97706" strokeDasharray="5 5" strokeWidth={1.5} />
            <ReferenceLine x={percentileMarkers.p95} stroke="#dc2626" strokeDasharray="5 5" strokeWidth={1.5} />
            <Area
              type="monotone"
              dataKey="probability"
              stroke="#7c3aed"
              strokeWidth={2.5}
              fill="url(#exceedGrad)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ThreatScenarioCharts;
